import { useEffect, useRef, useState } from "react";
import { countOnce } from "@/lib/api";
import { siteUrl } from "@/lib/site";
import { glyph } from "./logEngine";
import { Scramble } from "./LogText";

const LINK = /(https?:\/\/|www\.|\b[a-z0-9-]+\.(com|net|org|io|ai|co|app|me|ly)\b)/i;
const norm = (s) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();

// Client-side only: nothing typed here is stored, logged or sent anywhere.
export function matchReply(input, rules) {
  const q = norm(input);
  if (!q) return null;
  for (const r of rules) if (r.keys.some((k) => norm(k) === q)) return r.reply;
  for (const r of rules) {
    if (r.keys.some((k) => norm(k).length >= 3 && ` ${q} `.includes(` ${norm(k)} `))) return r.reply;
  }
  return null;
}

export function corrupt(text) {
  return Array.from(text, (c) => (c !== " " && Math.random() < 0.72 ? glyph() : c)).join("");
}

export function matchEgg(input, egg) {
  const q = norm(input);
  return !!(q && egg?.text && (egg.triggers || []).some((t) => norm(t) && ` ${q} `.includes(` ${norm(t)} `)));
}

// "who is m?" (if listed) at least half the time; the other phrases rotate at random.
export function pickGhost(phrases) {
  if (!phrases?.length) return "";
  const anchor = phrases.find((p) => norm(p) === "who is m");
  const rest = phrases.filter((p) => p !== anchor);
  if (anchor && (!rest.length || Math.random() < 0.5)) return anchor;
  return rest[Math.floor(Math.random() * rest.length)];
}

// Egg line: "the dial" becomes a quiet link to radio333 (?h=1 makes the dial react there). Origin-only referrer.
export function EggText({ text }) {
  const m = /the dial/i.exec(text);
  if (!m) return <Scramble text={text} />;
  const pre = text.slice(0, m.index), post = text.slice(m.index + m[0].length);
  return (
    <>
      {pre && <Scramble text={pre} />}
      <a data-testid="log-egg-dial-link" href={siteUrl("radio333.com", { h: "1" })} target="_blank" rel="noopener" referrerPolicy="origin"
        className="text-inherit no-underline hover:underline focus:underline"><Scramble text={m[0]} /></a>
      {post && <Scramble text={post} />}
    </>
  );
}

// Someone else on the channel: types a ghost phrase into the empty input, waits ~1.5 s, deletes it.
function useGhost(show, phrases, idle) {
  const [ghost, setGhost] = useState("");
  const idleRef = useRef(idle);
  idleRef.current = idle;
  const pool = useRef(phrases);
  pool.current = phrases;
  const has = !!phrases?.length;
  useEffect(() => {
    if (!show || !has) return undefined;
    const timers = [];
    const at = (ms, fn) => timers.push(setTimeout(fn, ms));
    const show_ = (s) => setGhost(idleRef.current() ? s : "");  // stops at once if the player starts typing
    const run = () => {
      if (!idleRef.current()) { at(10000, run); return; }
      const text = pickGhost(pool.current);
      let t = 0;
      for (let i = 1; i <= text.length; i++) { t += 90 + Math.random() * 120; at(t, () => show_(text.slice(0, i))); }
      t += 1500;
      for (let i = text.length - 1; i >= 0; i--) { t += 45; at(t, () => show_(text.slice(0, i))); }
      at(t + 60000 + Math.random() * 30000, run);
    };
    at(5000, run);
    return () => { timers.forEach(clearTimeout); setGhost(""); };
  }, [show, has]);
  return [ghost, setGhost];
}

// Desktop: any printable key anywhere types into the input. Phones: tapping the bottom 20% of the screen focuses it.
function useCapture(show, ref, typeKey) {
  const typeRef = useRef(typeKey);
  typeRef.current = typeKey;
  useEffect(() => {
    if (!show) return undefined;
    const busy = (t) => t?.closest?.("input, textarea, select, [contenteditable]");
    const key = (e) => {
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey || busy(e.target)) return;
      if (e.key === " " && e.target.closest?.("a, button")) return;
      e.preventDefault();
      ref.current?.focus();
      typeRef.current(e.key);
    };
    const tap = (e) => {
      if (e.clientY < window.innerHeight * 0.8 || e.target.closest?.("a, button") || busy(e.target)) return;
      ref.current?.focus();
    };
    const vv = window.visualViewport;
    const keep = () => { if (document.activeElement === ref.current) ref.current.scrollIntoView({ block: "end" }); };
    window.addEventListener("keydown", key);
    document.addEventListener("click", tap);
    vv?.addEventListener("resize", keep);
    return () => { window.removeEventListener("keydown", key); document.removeEventListener("click", tap); vv?.removeEventListener("resize", keep); };
  }, [show, ref]);
}

// Amber "M is listening > █" input at the bottom, shown only after 45 s on the page. Max 1 reply / 8 s, 280 chars, no links.
export function ReplyInput({ started, rules, egg, ghostPhrases, hint, prompt, onReply, onHint }) {
  const [show, setShow] = useState(false);
  const [val, setVal] = useState("");
  const last = useRef(0);
  const typedAt = useRef(0);
  const typedEver = useRef(false);
  const eggShown = useRef(false);
  const input = useRef(null);
  const [ghost, setGhost] = useGhost(show, ghostPhrases, () => !val && Date.now() - typedAt.current > 5000);
  const hintRef = useRef(onHint);
  hintRef.current = () => hint && onHint(hint);
  useEffect(() => {
    if (!started) return undefined;
    const ts = [setTimeout(() => { setShow(true); countOnce("log_input"); hintRef.current(); }, 45000),
      setTimeout(() => { if (!typedEver.current) hintRef.current(); }, 120000)];
    return () => ts.forEach(clearTimeout);
  }, [started]);
  const typed = () => {
    typedAt.current = Date.now();
    setGhost("");
    if (!typedEver.current) { typedEver.current = true; countOnce("log_typed"); }
  };
  useCapture(show, input, (ch) => { typed(); setVal((v) => (v + ch).slice(0, 280)); });
  if (!show) return null;
  const submit = (e) => {
    e.preventDefault();
    const text = val;
    setVal("");
    if (!text.trim() || LINK.test(text) || Date.now() - last.current < 8000) return;
    last.current = Date.now();
    if (!eggShown.current && matchEgg(text, egg)) { eggShown.current = true; countOnce("log_egg"); onReply(egg.text, "egg"); return; }
    const reply = matchReply(text, rules);
    if (reply) onReply(reply, false);
    else if (Math.random() < 1 / 3) onReply(corrupt(rules.length ? rules[Math.floor(Math.random() * rules.length)].reply : "no signal here"), true);
  };
  const change = (e) => { typed(); setVal(e.target.value); };
  const lift = () => setTimeout(() => input.current?.scrollIntoView({ block: "end" }), 300);
  return (
    <form onSubmit={submit} onClick={() => input.current?.focus()} className="log-reply log-reply-bar -mx-4 flex shrink-0 items-center gap-2 px-4 pb-4 pt-3 sm:-mx-10 sm:px-10" data-testid="log-reply-form" autoComplete="off">
      <span data-testid="log-reply-prompt" className="log-prompt shrink-0 whitespace-pre text-[color:var(--w1-amber)]">{(prompt || "> ").trimEnd()}</span>
      <span className="relative flex-1">
        <input ref={input} data-testid="log-reply-input" value={val} maxLength={280} onChange={change} onFocus={lift}
          aria-label="reply" spellCheck={false} autoCapitalize="off" autoCorrect="off" enterKeyHint="send"
          className={`log-reply-input w-full bg-transparent text-[color:var(--w1-amber)] outline-none ${val ? "" : "log-caret-off"}`} />
        {!val && (
          <span className="pointer-events-none absolute left-0 top-0 flex items-center text-[color:var(--w1-text)]">
            {ghost && <span data-testid="log-ghost-text" className="opacity-80">{ghost}</span>}
            <span data-testid="log-reply-cursor" className="w1-cursor log-prompt-cursor" />
          </span>
        )}
      </span>
    </form>
  );
}

// INTEGRITY 3%: 2 s blackout with a static burst, then either the reward video (then "radio333.com" for 10 s)
// or, with no video uploaded, M's final line decodes alone in the centre and holds.
export function ZeroEvent({ text, onDone, onPhase, audio, video }) {
  const [phase, setPhase] = useState("black");
  const [line, setLine] = useState(text);
  useEffect(() => { onPhase?.(phase); }, [phase]);  // eslint-disable-line react-hooks/exhaustive-deps
  const timers = useRef([]);
  const later = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const hold = (t, ms) => { setLine(t); setPhase("line"); later(ms, () => setPhase("fade")); later(ms + 1200, onDone); };
  useEffect(() => {
    audio?.burst();
    later(2000, () => {
      const v = video?.current;
      if (!v) { hold(text, 7000); return; }
      setPhase("video");
      v.currentTime = 0;
      v.play().catch(() => { v.muted = true; v.play().catch(() => hold(text, 7000)); });
    });
    return () => timers.current.splice(0).forEach(clearTimeout);  // eslint-disable-line react-hooks/exhaustive-deps
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const v = video?.current;
    if (!v) return undefined;
    const end = () => hold("radio333.com", 10000);
    const fail = () => { if (phase === "video") hold(text, 7000); };
    v.addEventListener("ended", end);
    v.addEventListener("error", fail);
    return () => { v.removeEventListener("ended", end); v.removeEventListener("error", fail); };
  }, [phase]);  // eslint-disable-line react-hooks/exhaustive-deps
  const reward = line === "radio333.com";
  return (
    <div data-testid="log-zero-event" data-phase={phase} className={`log-zero ${phase === "fade" ? "log-zero-fade" : ""}`}>
      {(phase === "line" || phase === "fade") && (
        <div data-testid="log-zero-line" className={`crt-glow max-w-[86vw] px-6 text-center tracking-[0.12em] ${reward ? "text-lg sm:text-2xl text-[color:var(--w1-amber)]" : "text-sm sm:text-base text-[color:var(--w1-text)]"}`}>
          <Scramble text={line} ms={900} /><span className="w1-cursor ml-1" />
        </div>
      )}
    </div>
  );
}

// The reward video lives in the page from the start so the start tap can unlock sound on phones.
export function RewardVideo({ vref, src, showing }) {
  return (
    <div data-testid="log-reward-frame" className={`log-reward ${showing ? "log-reward-on" : ""}`} aria-hidden={!showing}>
      <div className="log-reward-glitch">
        <video ref={vref} data-testid="log-reward-video" src={src} playsInline preload="auto" className="log-reward-video" />
        <span className="log-reward-scan" />
      </div>
    </div>
  );
}
